/* Treatment lists (Alle behandlinger, the categories, Skinlab): skin needs as one sticky row of chips, and a result
   line with the live count, Sorter and search. Liquid renders every card; this script only shows, hides and orders them.
   The page's main list (data-filter-url) keeps its state in the address with the Shop's names: ?behov, ?sok and
   ?sorter; the overview also keeps its category in ?kategori without navigating. A Laser facials type is its own collection page. Choices replace the history entry (as in Shop), so Back
   from a treatment, reload and shared links show the same view. The chosen need follows the category tabs. */
if (!customElements.get('laserlab-treatment-filter')) {
  customElements.define('laserlab-treatment-filter', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const q = selector => this.querySelector(selector);
      this.count = q('[data-filter-count]');
      this.bar = q('[data-filter-bar]');
      this.form = q('[data-filter-form]');
      this.input = q('[data-filter-input]');
      this.clear = q('[data-filter-clear]');
      this.hint = q('[data-filter-hint]');
      this.hintText = this.hint?.textContent.trim() || '';
      this.searchToggle = q('[data-filter-search-toggle]');
      this.scroller = q('[data-filter-chips]');
      this.needChips = Array.from(this.querySelectorAll('[data-filter-need-chip]'));
      this.needSelect = q('[data-filter-need-select]');
      this.needCaption = q('[data-filter-need-caption]');
      this.needPlaceholder = this.needSelect?.dataset.filterNeedPlaceholder || 'Hva vil du behandle?';
      this.needOptions = Array.from(this.needSelect?.options || []).filter(option => option.value);
      this.typeChips = Array.from(this.querySelectorAll('[data-filter-type-chip]'));
      this.typeAllURL = q('[data-filter-chips]')?.dataset.allUrl || '';
      this.sortSelect = q('[data-filter-sort]');
      this.sortCaption = q('[data-filter-sort-caption]');
      this.meta = q('[data-filter-meta]');
      this.isDirectory = this.hasAttribute('data-filter-directory');
      this.directoryGroups = this.isDirectory ? q('.ll-tgroups') : null;
      this.sortedGrid = this.isDirectory ? q('[data-filter-sorted-grid]') : null;
      this.sortedTitle = this.isDirectory ? q('[data-filter-sorted-title]') : null;
      this.resetAll = this.isDirectory ? q('[data-filter-reset-all]') : null;
      // The overview starts with a small selection in one category; other treatment lists stay unchanged.
      this.browseLimit = this.isDirectory ? Number(this.dataset.browserLimit) || 0 : 0;
      this.browseMore = this.browseLimit ? q('[data-browser-more]') : null;
      this.browseMoreWrap = this.browseMore?.closest('[data-browser-more-wrap]');
      this.browseMoreLabel = this.browseMore?.querySelector('[data-browser-more-label]');
      // Search sits at the far right of the sticky chip row (or, without the row, in the result line).
      this.searchHost = this.form?.closest('[data-filter-bar]') || this.meta;
      this.empty = q('[data-filter-empty]');
      // The consultation card (not a filter card): first in the plain list on tablets and desktop; last on phones and
      // whenever a need, type or search is chosen, so the treatments lead.
      this.consult = q('.ll-treatment-browser__item--consult');
      this.phoneMedia = window.matchMedia('(max-width: 767.98px)');
      this.tabs = Array.from(this.querySelectorAll('[data-tab-needs]'));
      this.syncURL = this.dataset.filterUrl === 'true';
      // Visible category links carry their own supported needs; they also work when
      // the old category tiles are absent. Featured lists do not rewrite navigation.
      if (this.syncURL) document.querySelectorAll('.ll-catbar__link[data-tab-needs], .header__menu a[data-tab-needs], #MenuDrawer a[data-tab-needs]').forEach(link => {
        if (!this.tabs.includes(link)) this.tabs.push(link);
      });
      // Only choices rendered in this instance can be restored from the URL. Other treatment lists keep their own options.
      const sortLabels = { recommended: 'Anbefalt', 'price-ascending': 'Laveste pris', 'price-descending': 'Høyeste pris', 'duration-ascending': 'Korteste tid' };
      this.sortCaptions = { recommended: sortLabels.recommended };
      Array.from(this.sortSelect?.options || []).forEach(option => {
        if (Object.hasOwn(sortLabels, option.value)) this.sortCaptions[option.value] = option.textContent.trim() || sortLabels[option.value];
      });
      // Alle behandlinger in groups (lister_rader, 11.10): each group's count follows the filter; an empty group hides.
      this.groups = Array.from(this.querySelectorAll('.ll-tgroup')).map(group => ({
        group, count: group.querySelector('[data-group-count]'), cards: Array.from(group.querySelectorAll('[data-filter-card]'))
      }));
      if (!this.count || !(this.input || this.needChips.length || this.needSelect || this.typeChips.length || this.sortSelect)) {
        // A list with nothing to choose (Laserhårfjerning once rolig_filterrad takes the list's own search away, 11.10)
        // shows every treatment. An old ?sok= link drops its word from the address, as the lists with chips do.
        const searchRemoved = (document.body.dataset.llFlags || '').split(' ').includes('rolig_filterrad');
        if (this.syncURL && !this.input && searchRemoved) {
          try {
            const url = new URL(location.href);
            if (url.searchParams.has('sok')) { url.searchParams.delete('sok'); history.replaceState(history.state, '', url.href); }
          } catch (_) { /* The list is shown in full either way. */ }
        }
        this.removeAttribute('data-restoring');
        return;
      }

      // Chips and the optional picker share the labels and descriptions from laserlab-shop-need.liquid.
      this.needLabels = {};
      this.needWords = {};
      this.needChips.forEach(chip => {
        const value = chip.dataset.filterNeedChip;
        const label = chip.querySelector('[data-filter-need-label]')?.textContent.trim() || value;
        this.needLabels[value] = label;
        this.needWords[value] = `${label} ${chip.dataset.description || ''}`;
      });
      this.needOptions.forEach(option => {
        this.needLabels[option.value] = option.dataset.filterNeedLabel || option.value;
        this.needWords[option.value] = `${this.needLabels[option.value]} ${option.dataset.description || ''}`;
      });
      this.needs = Object.keys(this.needLabels);

      this.cards = Array.from(this.querySelectorAll('[data-filter-card]')).map((card, index) => {
        let tags = [];
        try { tags = JSON.parse(card.dataset.filterTags || '[]'); } catch (_) { /* Cards without tags remain searchable. */ }
        const labels = Array.isArray(tags) ? tags.map(tag => String(tag)).filter(tag => !/^behov-/i.test(tag)).map(tag => tag.replace(/^tag__[^_]+_/, '')) : [];
        const needs = (card.dataset.needs || '').split(' ').filter(Boolean);
        const visibleCopy = Array.from(card.querySelectorAll('.product-card__title, .product-card__excerpt, .ll-treatment-card__family, .llt__new-heading, .llt__new-copy, .llt__new-badge'))
          .map(node => node.textContent).join(' ');
        // Needs are searchable too: «glød» finds the treatments tagged behov-glod. data-search-words adds the words
        // people use for each need («aldring», «t-sone», «utslett») and the treatment's own intro, focus and «Passer for»
        // (snippets/laserlab-search-words.liquid).
        const raw = `${visibleCopy} ${labels.join(' ')} ${needs.map(need => this.needWords[need] || '').join(' ')} ${card.dataset.searchWords || ''}`;
        const text = this.normalize(raw);
        const price = card.dataset.price ? Number(card.dataset.price) : NaN;
        return {
          card, index, raw, text, originalParent: card.parentElement,
          words: text.split(' '),
          // Directory categories use stable family keys, independent of editor block IDs and current grid parents.
          groups: this.isDirectory
            ? [card.dataset.directoryCategory || (card.dataset.treatmentCategory === 'skinlab' ? 'skinlab' : '')].filter(Boolean)
            : (card.dataset.filterGroups || '').split('|').filter(Boolean),
          needs,
          primary: card.dataset.needPrimary || '',
          price: Number.isFinite(price) ? price : null,
          duration: this.parseDuration(card.dataset.duration)
        };
      });
      this.orderKey = `original:${this.cards.map(card => card.index).join(',')}`;

      const pressedType = this.typeChips.find(chip => chip.getAttribute('aria-pressed') === 'true');
      this.defaultType = this.browseLimit
        ? this.typeChips.find(chip => chip.dataset.filterTypeChip !== 'all' && this.cards.some(card => card.groups.includes(chip.dataset.filterTypeChip)))?.dataset.filterTypeChip || 'all'
        : 'all';
      this.state = { type: this.browseLimit ? this.defaultType : pressedType?.dataset.filterTypeChip || 'all', need: '', query: '', sort: 'recommended', expanded: false };
      this.abort = new AbortController();
      const on = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: this.abort.signal });
      // Below this width the search is a button in the result line that opens the field there; desktop shows the field (CSS).
      this.compactMedia = window.matchMedia('(max-width: 1023px)');

      this.needChips.forEach(chip => on(chip, 'click', () => {
        const need = chip.dataset.filterNeedChip;
        this.state.need = this.state.need === need ? '' : need;
        this.commit();
        this.afterChoice(chip);
      }));
      on(this.needSelect, 'change', () => {
        this.state.need = this.needs.includes(this.needSelect.value) ? this.needSelect.value : '';
        this.commit();
        this.scrollToResults();
      });
      this.setupNeedPicker(on);
      this.typeChips.forEach(chip => on(chip, 'click', () => {
        const type = chip.dataset.filterTypeChip;
        this.state.type = this.isDirectory ? type : (this.state.type === type ? 'all' : type);
        if (this.browseLimit) this.state.expanded = type === 'all';
        // A category remains selectable after a need is chosen. Keep compatible needs, drop an incompatible one.
        if (this.isDirectory && this.state.need && !this.cards.some(card =>
          (type === 'all' || card.groups.includes(type)) && card.needs.includes(this.state.need))) this.state.need = '';
        this.commit();
        // A removed type chip disappears (Laser facials); keep focus in the row.
        if (chip.hidden) (this.needTrigger || this.needSelect || this.needChips.find(item => !item.disabled && !item.hidden) || this.searchToggle || this.input)?.focus({ preventScroll: true });
        this.afterChoice(chip.hidden ? null : chip);
      }));
      on(this.browseMore, 'click', () => {
        if (this.hasAttribute('data-browser-expanded')) {
          this.state = { ...this.state, need: '', query: '', sort: 'recommended', expanded: false };
          if (this.input) this.input.value = '';
          this.setSearchOpen(false);
          this.syncControls();
        } else this.state.expanded = true;
        this.commit();
        if (this.browseMoreWrap?.hidden) {
          const selected = this.typeChips.find(chip => chip.dataset.filterTypeChip === this.state.type);
          selected?.focus({ preventScroll: true });
          this.revealChip(selected);
        }
      });
      on(this.sortSelect, 'change', () => {
        this.state.sort = Object.hasOwn(this.sortCaptions, this.sortSelect.value) ? this.sortSelect.value : 'recommended';
        this.commit();
        this.scrollToResults();
      });

      if (this.input) {
        on(this.input, 'input', () => { this.state.query = this.input.value; this.commit({ quiet: true }); });
        on(this.input, 'search', () => { this.state.query = this.input.value; this.commit({ quiet: true }); });
        on(this.input, 'keydown', event => {
          if (event.key !== 'Escape' || !this.isSearchOpen()) return;
          event.preventDefault();
          event.stopPropagation();
          this.closeSearch();
        });
        on(this.form, 'submit', event => {
          event.preventDefault();
          this.state.query = this.input.value;
          this.commit();
          // Phones: Enter closes the keyboard so the results are visible.
          if (window.matchMedia('(max-width: 767px)').matches) this.input.blur();
        });
        on(this.clear, 'click', event => {
          event.preventDefault();
          if (this.isSearchOpen()) { this.closeSearch(); return; }
          this.input.value = '';
          this.state.query = '';
          this.commit();
          this.input.focus({ preventScroll: true });
        });
        on(this.searchToggle, 'click', () => this.openSearch());
        // The search examples show only while the field is in use. They fold away a moment after focus leaves,
        // so a tap below never lands on content that has just moved up.
        on(this.form, 'focusin', () => { clearTimeout(this.hintTimer); this.searchHost?.classList.add('is-hinting'); });
        on(this.form, 'focusout', () => {
          clearTimeout(this.hintTimer);
          this.hintTimer = setTimeout(() => { if (!this.form.contains(document.activeElement)) this.searchHost?.classList.remove('is-hinting'); }, 250);
          // An opened but empty search folds back when focus leaves it.
          requestAnimationFrame(() => {
            if (!this.isSearchOpen() || this.form.contains(document.activeElement) || this.state.query.trim()) return;
            this.setSearchOpen(false);
            this.render();
          });
        });
        on(this.compactMedia, 'change', () => {
          this.syncControls();
          this.render();
        });
        on(this.phoneMedia, 'change', () => this.render());
        // The spelling help loads after the page; a pending search is tried again with it.
        on(document, 'laserlab:search-terms', () => { if (this.state.query.trim()) this.render(); });
      }
      on(this, 'click', event => {
        if (!(event.target instanceof Element)) return;
        if (this.isDirectory && event.target.closest('[data-filter-reset-all]')) { this.clearFilters({ resetSort: true }); return; }
        if (event.target.closest('[data-filter-reset]')) this.clearFilters();
      });

      // The chip row stays under the header while the list scrolls; once it is stuck it casts the header's shadow.
      // With lister_rader (11.10) it scrolls with the page on phones: there it is never stuck.
      if (this.bar) {
        const onScroll = () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            const barStyle = getComputedStyle(this.bar);
            if (barStyle.position !== 'sticky') { this.bar.classList.remove('is-stuck'); return; }
            const top = parseFloat(barStyle.top) || 0;
            this.bar.classList.toggle('is-stuck', window.scrollY > 0 && this.bar.getBoundingClientRect().top <= top + .5);
          });
        };
        on(window, 'scroll', onScroll, { passive: true });
        on(window, 'resize', onScroll, { passive: true });
        onScroll();
      }

      if (this.syncURL) {
        on(window, 'popstate', () => this.readLocation());
        // Where the list was left, so Back to a filtered list can return to the same place (see restoreScroll).
        on(window, 'pagehide', () => {
          try { sessionStorage.setItem(this.scrollKey(), String(Math.round(window.scrollY))); } catch (_) { /* Private mode: the browser's own restore applies. */ }
        });
      }
      // Back from the page cache reads the address again; a normal load only puts the controls back in line with it
      // (browsers may restore form fields), so a search typed while the page loads is kept.
      on(window, 'pageshow', event => { if (event.persisted || !this.syncURL) this.readLocation(); else this.syncControls(); });

      this.classList.add('is-enhanced');
      this.initialized = true;
      this.readLocation();
      this.revealChip(this.needChips.find(chip => chip.getAttribute('aria-pressed') === 'true'));
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.closeNeedPicker();
      clearTimeout(this.writeTimer);
      clearTimeout(this.hintTimer);
      cancelAnimationFrame(this.scrollFrame);
      this.abort?.abort();
      this.initialized = false;
    }

    /* ---------- State and address ---------- */

    readLocation() {
      this.closeNeedPicker();
      const state = { ...this.state };
      if (this.syncURL) {
        let params;
        try { params = new URLSearchParams(location.search); } catch (_) { params = new URLSearchParams(); }
        state.query = this.input ? params.get('sok') || '' : '';
        const sort = params.get('sorter') || 'recommended';
        state.sort = this.sortSelect && Object.hasOwn(this.sortCaptions, sort) ? sort : 'recommended';
        const type = this.typeFromPath() || this.typeFromParam(params);
        if (type) state.type = type;
        else if (this.browseLimit) state.type = params.has('behov') || params.has('sok') || params.has('sorter') || params.get('vis') === 'alle' ? 'all' : this.defaultType;
        else if (this.isDirectory || (this.typeAllURL && this.pathOf(this.typeAllURL) === this.pathOf(location.href))) state.type = 'all';
        if (this.browseLimit) state.expanded = params.get('vis') === 'alle' || state.type === 'all';
        // The type first, then the need: a need none of the type's treatments has is dropped (a typed or old
        // /collections/laser-facials-classic?behov=arr shows Classic unfiltered, not «0 av …»), as render() hides its chip.
        const need = (params.get('behov') || '').toLocaleLowerCase('nb-NO');
        const typeCards = state.type === 'all' ? this.cards : this.cards.filter(card => card.groups.includes(state.type));
        state.need = this.needs.includes(need) && typeCards.some(card => card.needs.includes(need)) ? need : '';
      } else {
        state.query = this.input?.value || '';
        state.sort = this.sortSelect?.value || 'recommended';
      }
      this.state = state;
      this.syncControls();
      this.render();
      if (this.isDirectory) this.revealChip(this.typeChips.find(chip => chip.getAttribute('aria-pressed') === 'true'));
      this.writeState();
      this.removeAttribute('data-restoring');
      this.restoreScroll();
    }

    scrollKey() {
      return `ll-list-scroll:${location.pathname}${location.search}`;
    }

    /* Back to a filtered list that the browser loads again (no page cache): the cards are only in order once the
       choices are applied, so the inline script turned the browser's own restore off and the position is set here. */
    restoreScroll() {
      if (this.scrollRestored || !this.syncURL) return;
      this.scrollRestored = true;
      try {
        if (history.scrollRestoration !== 'manual') return;
        const saved = sessionStorage.getItem(this.scrollKey());
        if (saved !== null) window.scrollTo(0, Number(saved) || 0);
        history.scrollRestoration = 'auto';
      } catch (_) { /* Nothing to restore. */ }
    }

    // A Laser facials type is the page itself (/collections/laser-facials-pico); its chip carries that address.
    typeFromPath() {
      const here = this.pathOf(location.href);
      const match = this.typeChips.find(chip => chip.dataset.url && this.pathOf(chip.dataset.url) === here);
      return match ? match.dataset.filterTypeChip : '';
    }

    // A category without an address of its own (a Skinlab category) is ?kategori=<name>, so a link can open the list
    // on it (Om Laserlab links each Skinlab category this way).
    typeFromParam(params) {
      const wanted = (params.get('kategori') || '').toLocaleLowerCase('nb-NO');
      const match = wanted && this.typeChips.find(chip => !chip.dataset.url && chip.dataset.filterTypeChip === wanted);
      return match ? match.dataset.filterTypeChip : '';
    }

    pathOf(url) {
      try { return new URL(url, location.href).pathname.replace(/\/+$/, '') || '/'; } catch (_) { return ''; }
    }

    /* Writes the state to the address without adding history steps. Unknown values have already been dropped. */
    writeState() {
      if (this.syncURL) {
        try {
          const url = new URL(location.href);
          const set = (name, value) => { if (value) url.searchParams.set(name, value); else url.searchParams.delete(name); };
          set('behov', this.state.need);
          set('sok', this.state.query.trim());
          set('sorter', this.state.sort === 'recommended' ? '' : this.state.sort);
          const chip = this.typeChips.find(item => item.dataset.filterTypeChip === this.state.type);
          set('kategori', chip && !chip.dataset.url && (this.browseLimit || this.state.type !== 'all') ? this.state.type : '');
          if (this.browseLimit) set('vis', this.state.expanded ? 'alle' : '');
          const path = chip?.dataset.url ? this.pathOf(chip.dataset.url) : (this.state.type === 'all' && this.typeAllURL ? this.pathOf(this.typeAllURL) : '');
          if (path) url.pathname = path;
          if (url.href !== location.href) history.replaceState(history.state, '', url.href);
        } catch (_) { /* The list still works without an addressable URL. */ }
      }
      this.updateTabs();
    }

    /* The chosen need follows the customer to the categories that have it, and back to Alle behandlinger.
       Each tab lists its category's needs (data-tab-needs, «*» = all). */
    updateTabs() {
      const need = this.state.need;
      this.tabs.forEach(link => {
        let url;
        try { url = new URL(link.getAttribute('href'), location.href); } catch (_) { return; }
        if (url.origin !== location.origin) return;
        const needs = link.dataset.tabNeeds === '*' ? null : (link.dataset.tabNeeds || '').split(' ');
        if (need && (!needs || needs.includes(need))) url.searchParams.set('behov', need);
        else url.searchParams.delete('behov');
        const next = `${url.pathname}${url.search}${url.hash}`;
        if (link.getAttribute('href') !== next) link.setAttribute('href', next);
      });
    }

    /* Applies a change: cards, counts, controls and the address. quiet = typing (the address follows after a pause). */
    commit({ quiet = false } = {}) {
      this.render();
      clearTimeout(this.writeTimer);
      if (quiet) this.writeTimer = setTimeout(() => this.writeState(), 400);
      else this.writeState();
    }

    /* After a choice further down the list, the new result starts right under the sticky row. */
    afterChoice(chip) {
      this.revealChip(chip);
      this.scrollToResults();
    }

    scrollToResults() {
      if (!this.bar?.classList.contains('is-stuck') || !this.meta) return;
      const stickyTop = parseFloat(getComputedStyle(this.bar).top) || 0;
      const target = Math.round(this.meta.getBoundingClientRect().top + window.scrollY - stickyTop - this.bar.offsetHeight);
      if (window.scrollY > target + 1) window.scrollTo({ top: target, behavior: 'auto' });
    }

    // Keeps a chip fully in view in the swipe row without moving the page.
    revealChip(chip) {
      const list = chip?.closest('[data-filter-category-chips], [data-filter-chips]') || this.scroller;
      if (!chip || !list || chip.hidden || list.scrollWidth <= list.clientWidth) return;
      const chipBox = chip.getBoundingClientRect();
      const listBox = list.getBoundingClientRect();
      // The row's edge fade (--ll-reveal-gap, 32 px with rolig_filterrad, 11.10): a chosen chip stops clear of it.
      // Without the variable the gap is 0, the reveal as before.
      const fade = parseFloat(getComputedStyle(list).getPropertyValue('--ll-reveal-gap')) || 0;
      if (chipBox.left < listBox.left + fade) list.scrollLeft -= listBox.left - chipBox.left + 16 + fade;
      else if (chipBox.right > listBox.right - fade) list.scrollLeft += chipBox.right - listBox.right + 16 + fade;
    }

    clearFilters({ resetSort = false } = {}) {
      this.state = { ...this.state, type: this.browseLimit ? this.defaultType : 'all', need: '', query: '', expanded: false };
      if (resetSort || this.browseLimit) this.state.sort = 'recommended';
      if (this.input) this.input.value = '';
      this.setSearchOpen(false);
      this.syncControls();
      this.commit();
      const focus = this.browseLimit ? this.typeChips.find(chip => chip.dataset.filterTypeChip === this.defaultType) : resetSort
        ? (this.isDirectory && this.typeChips.find(chip => chip.dataset.filterTypeChip === 'all') || this.needTrigger || this.needSelect || this.needChips.find(chip => !chip.hidden && !chip.disabled) || this.sortSelect || this.input)
        : (this.needTrigger || this.needSelect || this.needChips[0] || this.searchToggle || this.input);
      focus?.focus({ preventScroll: true });
      if (this.isDirectory) this.revealChip(this.typeChips.find(chip => chip.dataset.filterTypeChip === (this.browseLimit ? this.defaultType : 'all')));
    }

    /* Controls follow the state (also after the browser restores form fields on Back or reload). */
    syncControls() {
      if (this.input && this.input.value !== this.state.query) this.input.value = this.state.query;
      if (this.sortSelect) this.sortSelect.value = this.state.sort;
      if (this.needSelect) this.needSelect.value = this.state.need;
      if (this.searchToggle) {
        if (this.state.query.trim()) this.setSearchOpen(true);
        else if (document.activeElement !== this.input) this.setSearchOpen(false);
      }
    }

    /* A native auto popover supplies light dismissal and Escape without a modal or custom menu role.
       Older browsers retain the native select. Both presentations use the same options and filter state. */
    setupNeedPicker(on) {
      const host = this.needSelect?.closest('.ll-tfilter__need');
      if (!host || typeof HTMLElement.prototype.showPopover !== 'function') return;
      this.needTrigger = host.querySelector('[data-filter-need-trigger]');
      this.needPanel = host.querySelector('[data-filter-need-panel]');
      if (!this.needTrigger) {
        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'll-tfilter__need-trigger';
        trigger.dataset.filterNeedTrigger = '';
        trigger.append(host.querySelector('.ll-tfilter__need-face'));
        const panel = document.createElement('div');
        panel.id = `${this.needSelect.id}-choices`;
        panel.className = 'll-tfilter__need-panel';
        panel.dataset.filterNeedPanel = '';
        panel.setAttribute('popover', 'auto');
        panel.setAttribute('role', 'group');
        panel.setAttribute('aria-label', this.needPlaceholder);
        Array.from(this.needSelect.options).forEach(option => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'll-tfilter__need-choice';
          button.dataset.filterNeedChoice = option.value;
          const mark = document.createElement('span');
          mark.className = 'll-tfilter__need-mark';
          mark.setAttribute('aria-hidden', 'true');
          mark.textContent = '✓';
          const label = document.createElement('span');
          label.className = 'll-tfilter__need-label';
          label.textContent = option.value ? this.needLabels[option.value] : option.textContent;
          const count = document.createElement('span');
          count.className = 'll-tfilter__need-count';
          count.dataset.filterNeedCount = '';
          count.setAttribute('aria-hidden', 'true');
          button.append(mark, label, count);
          panel.append(button);
        });
        trigger.setAttribute('popovertarget', panel.id);
        trigger.setAttribute('aria-controls', panel.id);
        trigger.setAttribute('aria-expanded', 'false');
        host.append(trigger, panel);
        this.needTrigger = trigger;
        this.needPanel = panel;
      }
      this.needChoices = Array.from(this.needPanel.querySelectorAll('[data-filter-need-choice]'));
      on(this.needPanel, 'beforetoggle', event => {
        if (event.newState === 'open') this.positionNeedPicker();
        this.needTrigger.setAttribute('aria-expanded', String(event.newState === 'open'));
      });
      on(this.needPanel, 'toggle', () => this.needTrigger.setAttribute('aria-expanded', String(this.needPanel.matches(':popover-open'))));
      on(this.needPanel, 'click', event => {
        const choice = event.target.closest('[data-filter-need-choice]');
        if (!choice || choice.disabled) return;
        this.state.need = choice.dataset.filterNeedChoice;
        this.commit();
        this.closeNeedPicker(true);
        this.scrollToResults();
      });
      // Safari can focus the page's main container while tapping an already-focused trigger.
      // Native light dismissal owns pointer interactions; Tab-away owns keyboard focus dismissal.
      on(document, 'pointerdown', event => { this.needPointerInside = host.contains(event.target); }, { capture: true });
      on(document, 'keydown', event => {
        this.needPointerInside = false;
        if (event.key !== 'Escape' || !this.needPanel.matches(':popover-open')) return;
        event.preventDefault();
        this.closeNeedPicker(true);
      }, { capture: true });
      on(host, 'keydown', event => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        const choices = this.needChoices.filter(button => !button.hidden && !button.disabled);
        if (!choices.length) return;
        event.preventDefault();
        if (!this.needPanel.matches(':popover-open')) this.needPanel.showPopover();
        const current = choices.indexOf(document.activeElement);
        const selected = choices.findIndex(button => button.getAttribute('aria-pressed') === 'true');
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? choices.length - 1 : current < 0
          ? (selected >= 0 ? selected : event.key === 'ArrowUp' ? choices.length - 1 : 0)
          : (current + (event.key === 'ArrowUp' ? -1 : 1) + choices.length) % choices.length;
        const itemBox = choices[index].getBoundingClientRect();
        const panelBox = this.needPanel.getBoundingClientRect();
        if (itemBox.top < panelBox.top + 6) this.needPanel.scrollTop -= panelBox.top + 6 - itemBox.top;
        else if (itemBox.bottom > panelBox.bottom - 6) this.needPanel.scrollTop += itemBox.bottom - panelBox.bottom + 6;
        choices[index].focus({ preventScroll: true });
      });
      on(document, 'focusin', event => { if (!this.needPointerInside && !host.contains(event.target)) this.closeNeedPicker(); });
      on(document, 'scroll', event => { if (!this.needPanel.contains(event.target)) this.closeMovedNeedPicker(); }, { capture: true, passive: true });
      on(window, 'resize', () => this.closeMovedNeedPicker());
      on(window.visualViewport, 'resize', () => this.closeMovedNeedPicker());
      on(window.visualViewport, 'scroll', () => this.closeMovedNeedPicker());
      this.needSelect.hidden = true;
      host.querySelector('label')?.setAttribute('hidden', '');
    }

    positionNeedPicker() {
      const view = window.visualViewport;
      const left = view?.offsetLeft || 0;
      const top = view?.offsetTop || 0;
      const width = Math.min(340, (view?.width || window.innerWidth) - 40);
      const box = this.needTrigger.getBoundingClientRect();
      const headerBottom = document.querySelector('.header-sticky')?.getBoundingClientRect().bottom || top;
      const categoryBox = document.querySelector('.ll-catbar')?.getBoundingClientRect();
      const categoryBottom = categoryBox?.height && categoryBox.bottom > top && categoryBox.bottom <= box.top ? categoryBox.bottom : top;
      const above = box.top - Math.max(top + 8, headerBottom + 8, categoryBottom + 8) - 6;
      const below = top + (view?.height || window.innerHeight) - box.bottom - 14;
      const upward = below < 220 && above > below;
      Object.assign(this.needPanel.style, {
        width: `${width}px`,
        left: `${Math.max(left + 20, Math.min(box.left + box.width / 2 - width / 2, left + (view?.width || window.innerWidth) - 20 - width))}px`,
        top: upward ? 'auto' : `${box.bottom + 6}px`,
        bottom: upward ? `${window.innerHeight - box.top + 6}px` : 'auto',
        maxHeight: `${Math.max(44, Math.min(420, upward ? above : below))}px`
      });
      this.needOpenGeometry = this.needPickerGeometry();
    }

    needPickerGeometry() {
      const view = window.visualViewport;
      const box = this.needTrigger.getBoundingClientRect();
      return [window.scrollX, window.scrollY, view?.offsetLeft || 0, view?.offsetTop || 0,
        view?.width || window.innerWidth, view?.height || window.innerHeight, box.left, box.top];
    }

    closeMovedNeedPicker() {
      if (!this.needPanel.matches(':popover-open')) return;
      // A delayed Safari scroll event from focusing the trigger must not close a newly opened panel.
      if (this.needPickerGeometry().some((value, index) => Math.abs(value - this.needOpenGeometry[index]) > .5)) this.closeNeedPicker();
    }

    closeNeedPicker(restoreFocus = false) {
      if (!this.needPanel?.isConnected || !this.needPanel.matches(':popover-open')) return;
      this.needPanel.hidePopover();
      if (restoreFocus) this.needTrigger.focus({ preventScroll: true });
    }

    /* ---------- Search in the result line (opens in the line on phones and tablets) ---------- */

    isSearchOpen() {
      return Boolean(this.searchToggle && this.searchHost?.classList.contains('is-searching'));
    }

    setSearchOpen(open) {
      if (!this.searchToggle || !this.searchHost) return;
      this.searchHost.classList.toggle('is-searching', open);
      this.searchToggle.setAttribute('aria-expanded', String(open));
    }

    openSearch() {
      this.setSearchOpen(true);
      this.render();
      this.input?.focus({ preventScroll: true });
    }

    closeSearch() {
      if (this.input) this.input.value = '';
      this.state.query = '';
      this.setSearchOpen(false);
      this.commit();
      if (this.searchToggle?.getClientRects().length) this.searchToggle.focus({ preventScroll: true });
      else this.input?.focus({ preventScroll: true });
    }

    /* ---------- Matching, counts and order ---------- */

    normalize(value) {
      return String(value).toLocaleLowerCase('nb-NO')
        .replace(/æ/g, 'ae').replace(/ø/g, 'o')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim();
    }

    // Use only explicitly advertised minutes. Ranges, bare numbers and other units remain unknown, rather than guessed.
    parseDuration(value) {
      const match = String(value || '').trim().match(/^(?:fra\s+)?(\d+(?:[.,]\d+)?)\s*min(?:utt(?:er)?)?\.?$/iu);
      if (!match) return null;
      const minutes = Number(match[1].replace(',', '.'));
      return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
    }

    // A term matches inside card text, or by its stem ("rynker" -> "rynkebehandling").
    // A term finds a word that begins with it (so a word still being typed already finds «pico»), or an inflection or
    // compound of it (stems). Never letters inside a word: «ringer» must not find «pigmentforandringer» (28.09).
    termMatches(card, term) {
      if (card.words.some(word => word.startsWith(term))) return true;
      const search = window.LaserlabSearchTerms;
      return Boolean(search) && search.stems(term).some(stem => card.words.some(word => search.stemMatches(word, stem)));
    }

    // 0 when every term is a whole word on the card (or that word with a short ending: «rynke», «rynker»), else 1.
    // Whole-word answers come first: «akne» shows Acne Advanced before the treatments for «aknearr».
    queryTier(card, terms) {
      const search = window.LaserlabSearchTerms;
      const whole = (word, value) => word === value || (word.startsWith(value) && word.length - value.length <= 2);
      const exact = term => card.words.some(word => whole(word, term)) || (Boolean(search) && search.stems(term).some(stem => card.words.some(word => whole(word, stem))));
      return terms.every(exact) ? 0 : 1;
    }

    // Misspelled or English words ("brazillian", "armpit") fall back to the words on these cards.
    searchTerms(pool) {
      const query = this.state.query.trim();
      const terms = this.normalize(query).split(' ').filter(Boolean);
      const search = window.LaserlabSearchTerms;
      if (!terms.length || !search || pool.some(card => terms.every(term => this.termMatches(card, term)))) return { terms, corrected: '' };
      if (!this.searchIndex) this.searchIndex = search.createIndex(this.cards.map(card => card.raw));
      const correction = search.correct(query, this.searchIndex);
      if (!correction.changed) return { terms, corrected: '' };
      const corrected = this.normalize(correction.query).split(' ').filter(Boolean);
      return pool.some(card => corrected.every(term => this.termMatches(card, term))) ? { terms: corrected, corrected: correction.query } : { terms, corrected: '' };
    }

    label(count) {
      return `${count} ${count === 1 ? 'behandling' : 'behandlinger'}`;
    }

    render() {
      const focusedElement = this.isDirectory ? document.activeElement : null;
      const focusedCard = focusedElement && this.cards.find(({ card }) => card.contains(focusedElement));
      const { type, need } = this.state;
      const browseExpanded = this.browseLimit && (this.state.expanded || type === 'all' || Boolean(need) || Boolean(this.state.query.trim()) || this.state.sort !== 'recommended');
      const inType = (card, value = type) => value === 'all' || card.groups.includes(value);
      const hasNeed = (card, value = need) => !value || card.needs.includes(value);
      const { terms, corrected } = this.searchTerms(this.cards.filter(card => inType(card) && hasNeed(card)));
      const matchesQuery = card => terms.every(term => this.termMatches(card, term));
      let matches = 0;
      this.queryTiers = new Map();
      this.cards.forEach(card => {
        const visible = inType(card) && hasNeed(card) && matchesQuery(card);
        if (visible) matches += 1;
        card.card.hidden = !visible || Boolean(this.browseLimit && !browseExpanded && matches > this.browseLimit);
        if (visible && terms.length) this.queryTiers.set(card, this.queryTier(card, terms));
      });

      // Counts per chip answer "what do I get if I choose this", given the other choices.
      const needPool = this.cards.filter(card => inType(card) && matchesQuery(card));
      const typePool = this.cards.filter(card => hasNeed(card) && matchesQuery(card));
      const setCount = (chip, count) => {
        const node = chip.querySelector('[data-filter-chip-count]');
        if (!node) return;
        const text = String(count);
        if (node.firstChild?.nodeType === Node.TEXT_NODE) { if (node.firstChild.nodeValue !== text) node.firstChild.nodeValue = text; }
        else node.prepend(text);
        const spoken = node.querySelector('.visually-hidden');
        if (spoken) spoken.textContent = ` ${count === 1 ? 'behandling' : 'behandlinger'}`;
      };
      const typeCards = type === 'all' ? this.cards : this.cards.filter(card => inType(card));
      this.needChips.forEach(chip => {
        const value = chip.dataset.filterNeedChip;
        const count = needPool.filter(card => card.needs.includes(value)).length;
        const pressed = value === need;
        chip.setAttribute('aria-pressed', String(pressed));
        // A need none of this type's treatments has is not offered (Glow has no Arr); a 0 from a search stays greyed,
        // so the row does not jump while typing.
        chip.hidden = !pressed && !typeCards.some(card => card.needs.includes(value));
        chip.disabled = !pressed && count === 0;
        setCount(chip, count);
      });
      if (this.needSelect) {
        this.needOptions.forEach(option => {
          const value = option.value;
          const count = needPool.filter(card => card.needs.includes(value)).length;
          const selected = value === need;
          option.hidden = !selected && !typeCards.some(card => card.needs.includes(value));
          option.disabled = !selected && count === 0;
          option.textContent = `${this.needLabels[value]} (${count})`;
        });
        this.needSelect.value = need;
        this.needSelect.closest('.ll-tfilter__need')?.toggleAttribute('data-selected', Boolean(need));
        if (this.needCaption) this.needCaption.textContent = this.needLabels[need] || this.needPlaceholder;
        if (this.needTrigger) {
          this.needTrigger.setAttribute('aria-label', need ? `${this.needPlaceholder} ${this.needLabels[need]}` : this.needPlaceholder);
          this.needChoices.forEach(button => {
            const value = button.dataset.filterNeedChoice;
            const option = Array.from(this.needSelect.options).find(item => item.value === value);
            button.hidden = option.hidden;
            button.disabled = option.disabled;
            button.setAttribute('aria-pressed', String(value === need));
            const count = value ? needPool.filter(card => card.needs.includes(value)).length : needPool.length;
            const node = button.querySelector('[data-filter-need-count]');
            node.textContent = String(count);
            button.setAttribute('aria-label', `${value ? this.needLabels[value] : 'Alle behandlinger'}, ${this.label(count)}`);
          });
        }
      }
      if (this.isDirectory && this.bar) this.bar.hidden = !this.input && !this.needChips.some(chip => !chip.hidden);
      this.typeChips.forEach(chip => {
        const value = chip.dataset.filterTypeChip;
        const pressed = value === type;
        chip.setAttribute('aria-pressed', String(pressed));
        // A Laser facials type is only a chip while it is chosen (from its own page).
        if (chip.hasAttribute('data-chip-auto')) { chip.hidden = !pressed; return; }
        const count = typePool.filter(card => inType(card, value)).length;
        chip.disabled = this.isDirectory ? !this.cards.some(card => inType(card, value)) : !pressed && count === 0;
        setCount(chip, count);
      });
      if (this.browseLimit) {
        this.toggleAttribute('data-browser-expanded', Boolean(browseExpanded));
        if (this.bar && !browseExpanded) this.bar.hidden = true;
        const label = this.typeChips.find(chip => chip.dataset.filterTypeChip === type)?.textContent.trim().toLocaleLowerCase('nb-NO') || 'behandlinger';
        if (this.browseMoreLabel) this.browseMoreLabel.textContent = browseExpanded ? 'Vis utvalgte' : `Se alle ${label}`;
        this.browseMore?.setAttribute('aria-expanded', String(Boolean(browseExpanded)));
        if (this.browseMoreWrap) this.browseMoreWrap.hidden = type === 'all' || (!browseExpanded && matches <= this.browseLimit);
      }
      this.bar?.classList.toggle('has-choice', Boolean(need) || type !== 'all');
      this.order();
      const globallySorted = Boolean(this.directoryGroups && this.sortedGrid && this.state.sort !== 'recommended');
      if (this.directoryGroups) this.directoryGroups.hidden = globallySorted;
      if (this.sortedGrid) this.sortedGrid.hidden = !globallySorted;
      if (this.sortedTitle) this.sortedTitle.hidden = !globallySorted;
      this.groups.forEach(({ group, count, cards }) => {
        const shown = cards.filter(card => !card.hidden).length;
        group.hidden = shown === 0;
        const text = this.label(shown);
        if (count && count.textContent !== text) count.textContent = text;
      });
      // Moving a focused link between the grouped and sorted lists can blur it. Keep focus when its card is still shown.
      if (focusedCard && !focusedCard.card.hidden && document.activeElement !== focusedElement) focusedElement.focus?.({ preventScroll: true });

      const active = Boolean(this.state.query.trim()) || type !== 'all' || Boolean(need);
      if (this.resetAll) this.resetAll.hidden = this.browseLimit ? !browseExpanded : !active && this.state.sort === 'recommended';
      const consultGrid = this.consult?.parentElement;
      if (consultGrid) {
        const last = active || this.phoneMedia.matches;
        // Alle in groups (lister_rader, 11.10): the consultation has a one-card grid of its own above the groups, so
        // that grid moves after the groups, not the card inside it.
        const grouped = consultGrid.classList.contains('ll-tgroups__consult');
        const node = grouped ? consultGrid : this.consult;
        const host = node.parentElement;
        if (host && last && host.lastElementChild !== node) host.appendChild(node);
        if (host && !last && host.firstElementChild !== node) host.prepend(node);
      }
      const total = this.cards.length;
      const pageOnly = this.dataset.filterPageOnly === 'true' ? ' på denne siden' : '';
      const noun = total === 1 ? 'behandling' : 'behandlinger';
      this.count.textContent = this.browseLimit ? this.label(matches) : active ? `${matches} av ${total} ${noun}${pageOnly}` : `${total} ${noun}${pageOnly}`;
      if (corrected) {
        const spoken = document.createElement('span');
        spoken.className = 'visually-hidden';
        spoken.textContent = `, treff for ${corrected}`;
        this.count.append(spoken);
      }
      if (this.hint) {
        this.hint.textContent = corrected ? `Viser treff for «${corrected}».` : this.hintText;
        this.hint.toggleAttribute('data-correction', Boolean(corrected));
      }
      if (this.clear) {
        const open = this.isSearchOpen();
        this.clear.hidden = !this.input.value && !open;
        this.clear.setAttribute('aria-label', open ? 'Lukk og tøm søket' : 'Tøm søket');
      }
      if (this.empty) this.empty.hidden = !active || matches > 0;
      if (this.sortCaption) this.sortCaption.textContent = this.sortCaptions[this.state.sort];
      // The sort icon in the row is filled while a sort other than Anbefalt is chosen.
      this.sortSelect?.closest('.ll-card-filter__sort')?.toggleAttribute('data-active', this.state.sort !== 'recommended');
    }

    /* The default keeps the clinic's own order within each original category. A chosen need's primary cards come first.
       Directory price/time choices move the actual cards into one shared list; reverting restores their original parents.
       Other treatment lists keep sorting inside their own list. Unknown price or time always goes last. */
    order() {
      const { sort, need } = this.state;
      const tier = card => (need && card.primary === need ? 0 : 1);
      // While searching, the best answers lead (queryTier); otherwise the clinic's order.
      const relevance = card => this.queryTiers?.get(card) ?? 0;
      const sorted = [...this.cards].sort((a, b) => {
        if (sort === 'price-ascending') return ((a.price ?? Infinity) - (b.price ?? Infinity)) || a.index - b.index;
        if (sort === 'price-descending') return ((b.price ?? -Infinity) - (a.price ?? -Infinity)) || a.index - b.index;
        if (sort === 'duration-ascending') return ((a.duration ?? Infinity) - (b.duration ?? Infinity)) || a.index - b.index;
        return relevance(a) - relevance(b) || tier(a) - tier(b) || a.index - b.index;
      });
      const destination = this.directoryGroups && this.sortedGrid && sort !== 'recommended' ? this.sortedGrid : null;
      // Include the destination: changing view must move cards even if their relative order happens to stay the same.
      const key = `${destination ? 'flat' : 'original'}:${sorted.map(card => card.index).join(',')}`;
      if (key === this.orderKey) return;
      this.orderKey = key;
      sorted.forEach(({ card, originalParent }) => (destination || originalParent)?.appendChild(card));
    }
  });
}
